import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import StatusBadge from "@/components/StatusBadge";
import {
  Check,
  X,
  Truck,
  User,
  Image as ImageIcon,
  FileText,
  MapPin,
  Clock,
  PenLine,
  Download,
  Hash,
  ClipboardCheck,
  RefreshCcw,
  BadgeCheck,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString("pt-BR");
};

export default function Approvals() {
  const { user } = useAuth();

  const [tasks, setTasks] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [selected, setSelected] = useState(null);
  const [logs, setLogs] = useState([]);
  const [zoomPhoto, setZoomPhoto] = useState(null);
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [processing, setProcessing] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try {
      setLoading(true);

      const [tasksResponse, collaboratorsResponse] =
        await Promise.all([
          api.get("/tasks"),
          api.get("/collaborators?active_only=false"),
        ]);

      setTasks(
        Array.isArray(tasksResponse.data)
          ? tasksResponse.data
          : []
      );

      setCollaborators(
        Array.isArray(collaboratorsResponse.data)
          ? collaboratorsResponse.data
          : []
      );
    } catch (e) {
      toast.error(
        formatApiError(e.response?.data?.detail) ||
          "Não foi possível carregar as inspeções."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const awaitingApproval = tasks.filter(
    (task) => task.status === "Enviada"
  );

  const awaitingCorrection = tasks.filter(
    (task) => task.status === "Pendente Correção"
  );

  const approved = tasks.filter(
    (task) => task.status === "Aprovada"
  );

  const open = async (task) => {
    setSelected(task);
    setRejectMode(false);
    setReason("");
    setLogs([]);
    setLoadingLogs(true);

    try {
      const { data } = await api.get(`/tasks/${task.id}/logs`);
      setLogs(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(
        formatApiError(e.response?.data?.detail) ||
          "Não foi possível carregar os dados da inspeção."
      );
    } finally {
      setLoadingLogs(false);
    }
  };

  const closeSelected = () => {
    setSelected(null);
    setLogs([]);
    setRejectMode(false);
    setReason("");
  };

  const approve = async () => {
    if (!selected || selected.status !== "Enviada") {
      return;
    }

    try {
      setProcessing(true);

      await api.post(`/tasks/${selected.id}/approve`);

      toast.success("Inspeção aprovada");

      closeSelected();
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setProcessing(false);
    }
  };

  const reject = async () => {
    if (!selected || selected.status !== "Enviada") {
      return;
    }

    if (!reason.trim()) {
      toast.error("Descreva o motivo da correção");
      return;
    }

    try {
      setProcessing(true);

      await api.post(`/tasks/${selected.id}/reject`, {
        reason: reason.trim(),
      });

      toast.success("Inspeção enviada para correção");

      closeSelected();
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setProcessing(false);
    }
  };

  const deleteInspection = async () => {
    if (!selected || !isAdmin) {
      return;
    }

    const identifier =
      selected.protocol_number ||
      selected.id?.slice(0, 8) ||
      "";

    const confirmed = window.confirm(
      `Tem certeza que deseja excluir a inspeção ${identifier}?\n\nEssa ação removerá a inspeção e o histórico de envios. Não será possível desfazer.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setProcessing(true);

      await api.delete(
        `/tasks/${selected.id}`
      );

      toast.success(
        "Inspeção excluída"
      );

      closeSelected();
      await load();
    } catch (e) {
      toast.error(
        formatApiError(
          e.response?.data?.detail
        ) ||
          "Não foi possível excluir a inspeção."
      );
    } finally {
      setProcessing(false);
    }
  };


  const exportPdf = async () => {
    if (!selected) {
      return;
    }

    try {
      const token = localStorage.getItem("fc_token");

      const response = await fetch(
        `${API}/tasks/${selected.id}/pdf?_t=${Date.now()}`,
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Falha ao gerar PDF");
      }

      const blob = await response.blob();
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement("a");

      anchor.href = href;
      anchor.download = `inspecao_${
        selected.protocol_number || selected.id.slice(0, 8)
      }.pdf`;

      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      URL.revokeObjectURL(href);
    } catch {
      toast.error("Não foi possível exportar o PDF");
    }
  };

  const renderDefects = (defects, answerIndex) => {
    if (!defects?.length) {
      return null;
    }

    return (
      <div className="mt-2 space-y-2 pl-3 border-l-2 border-red-500/40">
        {defects.map((defect, defectIndex) => (
          <div
            key={`${answerIndex}-${defectIndex}`}
            className="p-2 rounded-md bg-red-500/5"
            data-testid={`defect-item-${answerIndex}-${defectIndex}`}
          >
            <div className="text-xs font-semibold text-red-500 flex items-center gap-1">
              <ImageIcon className="w-3 h-3" />
              Defeito: {defect.label}
              {defect.response &&
              defect.response !== defect.label
                ? ` — ${defect.response}`
                : ""}
            </div>

            {defect.photo && (
              <img
                src={defect.photo}
                onClick={() =>
                  setZoomPhoto(defect.photo)
                }
                className="mt-2 w-40 h-28 object-cover rounded-md border border-border cursor-zoom-in"
                alt={defect.label || "Defeito"}
                data-testid={`defect-photo-${answerIndex}-${defectIndex}`}
              />
            )}

            {defect.notes && (
              <div className="text-xs mt-2 text-foreground">
                {defect.notes}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderAnswer = (answer, index) => {
    const value = answer.value;
    const defects = answer.item_defects || [];

    if (answer.type === "signature") {
      const signatureValue =
        answer.signature || answer.value;

      return (
        <div
          key={index}
          className="border-b border-border py-2"
          data-testid={`answer-${index}`}
        >
          <div className="text-sm font-semibold mb-2">
            {answer.question}
          </div>

          {signatureValue ? (
            <img
              src={signatureValue}
              alt="Assinatura"
              className="max-w-xs h-20 object-contain bg-white rounded-md border border-border p-1"
            />
          ) : (
            <div className="text-sm text-muted-foreground">
              —
            </div>
          )}
        </div>
      );
    }

    if (
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === "object" &&
      value[0]?.item !== undefined
    ) {
      return (
        <div
          key={index}
          className="border-b border-border pb-3"
          data-testid={`answer-${index}`}
        >
          <div className="text-sm font-semibold mb-2">
            {answer.question}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs border border-border rounded-md">
              <thead className="bg-secondary">
                <tr>
                  <th className="p-2 text-left">
                    Item
                  </th>

                  <th className="p-2 text-left">
                    Resposta
                  </th>
                </tr>
              </thead>

              <tbody>
                {value.map((pair, pairIndex) => {
                  const response =
                    String(
                      pair.response || ""
                    ).toLowerCase();

                  const isNegative = [
                    "defeito",
                    "não",
                    "nao",
                    "atenção",
                    "atencao",
                    "avaria",
                    "reprovado",
                    "critico",
                    "crítico",
                  ].includes(response);

                  const isOk =
                    response === "ok" ||
                    response === "conforme";

                  return (
                    <tr
                      key={pairIndex}
                      className="border-t border-border"
                    >
                      <td className="p-2">
                        {pair.item}
                      </td>

                      <td
                        className={`p-2 ${
                          isNegative
                            ? "text-red-500 font-bold"
                            : isOk
                            ? "text-emerald-500 font-bold"
                            : ""
                        }`}
                      >
                        {pair.response}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {renderDefects(defects, index)}
        </div>
      );
    }

    let display;

    if (
      answer.type === "vehicle_select" &&
      value
    ) {
      const vehicle = selected?.vehicle;

      display =
        vehicle && vehicle.id === value
          ? `#${vehicle.fleet_number} · ${
              vehicle.plate
            } · ${
              (vehicle.marca
                ? `${vehicle.marca} `
                : "") + vehicle.model
            }`
          : `Veículo ${String(value).slice(
              0,
              8
            )}...`;
    } else if (
      answer.type ===
        "collaborator_select" &&
      value
    ) {
      const collaborator =
        collaborators.find(
          (item) =>
            item.id === value
        );

      display = collaborator
        ? `${collaborator.name}${
            collaborator.role
              ? ` · ${collaborator.role}`
              : ""
          }`
        : `Colaborador ${String(
            value
          ).slice(0, 8)}...`;
    } else if (value === "ok") {
      display = "✓ Conforme";
    } else if (value === "defect") {
      display = "✗ Defeito";
    } else if (Array.isArray(value)) {
      display = value.join(", ");
    } else if (
      value &&
      typeof value === "object"
    ) {
      display = Object.entries(value)
        .map(
          ([key, itemValue]) =>
            `${key}: ${itemValue}`
        )
        .join(", ");
    } else {
      display = String(value ?? "—");
    }

    const isNegative =
      typeof value === "string" &&
      [
        "defeito",
        "não",
        "nao",
        "atenção",
        "atencao",
        "avaria",
        "defect",
        "reprovado",
      ].includes(value.toLowerCase());

    return (
      <div
        key={index}
        className="border-b border-border py-2"
        data-testid={`answer-${index}`}
      >
        <div className="flex items-start justify-between gap-4 text-sm">
          <span>
            {answer.question}
          </span>

          <span
            className={`font-semibold text-right ${
              isNegative
                ? "text-red-500"
                : value === "ok"
                ? "text-emerald-500"
                : ""
            }`}
          >
            {display}
          </span>
        </div>

        {renderDefects(defects, index)}
      </div>
    );
  };

  const InspectionCard = ({ task }) => {
    const needsCorrection =
      task.status === "Pendente Correção";

    return (
      <button
        type="button"
        onClick={() => open(task)}
        className={`w-full text-left rounded-lg border p-4 transition-colors ${
          needsCorrection
            ? "border-red-500/30 hover:border-red-500/70"
            : "border-border hover:border-primary"
        }`}
        data-testid={`inspection-card-${task.id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-display font-bold leading-tight">
              {task.template?.title ||
                "Inspeção"}
            </div>

            {task.protocol_number && (
              <div className="mt-1 text-[10px] uppercase tracking-widest text-primary font-mono flex items-center gap-1">
                <Hash className="w-3 h-3" />
                {task.protocol_number}
              </div>
            )}
          </div>

          <StatusBadge
            status={task.status}
          />
        </div>

        {task.vehicle ? (
          <div className="text-sm text-muted-foreground flex items-center gap-1.5 font-mono mt-3">
            <Truck className="w-3.5 h-3.5" />
            #{task.vehicle.fleet_number} ·{" "}
            {task.vehicle.plate}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground mt-3 italic">
            Sem veículo vinculado
          </div>
        )}

        {needsCorrection &&
          task.reject_reason && (
            <div className="mt-3 text-xs rounded-md border border-red-500/30 bg-red-500/5 p-2 text-red-600 dark:text-red-400">
              <b>Correção:</b>{" "}
              {task.reject_reason}
            </div>
          )}

        <div className="text-xs text-muted-foreground mt-3">
          {task.status === "Aprovada"
            ? `Aprovada em ${formatDate(
                task.reviewed_at ||
                  task.submitted_at
              )}`
            : `Enviada em ${formatDate(
                task.submitted_at ||
                  task.created_at
              )}`}
        </div>
      </button>
    );
  };

  const StatusSection = ({
    title,
    description,
    icon: Icon,
    tasks: sectionTasks,
    emptyMessage,
  }) => (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-primary" />
          </div>

          <div>
            <h2 className="font-display font-bold text-lg">
              {title}
            </h2>

            <p className="text-xs text-muted-foreground mt-0.5">
              {description}
            </p>
          </div>
        </div>

        <Badge
          variant="outline"
          className="font-mono"
        >
          {sectionTasks.length}
        </Badge>
      </div>

      {sectionTasks.length === 0 ? (
        <div className="py-7 text-center text-sm text-muted-foreground border border-dashed border-border rounded-md">
          {emptyMessage}
        </div>
      ) : (
        <div className="space-y-3">
          {sectionTasks.map((task) => (
            <InspectionCard
              key={task.id}
              task={task}
            />
          ))}
        </div>
      )}
    </Card>
  );

  return (
    <div
      className="p-6 max-w-6xl mx-auto"
      data-testid="approvals-page"
    >
      <PageHeader
        title="Inspeções"
        subtitle="Acompanhe inspeções aguardando análise, em correção e aprovadas"
      />

      {loading ? (
        <Card className="p-10 text-center">
          <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="font-semibold">
            Carregando inspeções...
          </div>
        </Card>
      ) : (
        <div className="space-y-5">
          <StatusSection
            title="Aguardando aprovação"
            description="Inspeções enviadas e prontas para análise."
            icon={ClipboardCheck}
            tasks={awaitingApproval}
            emptyMessage="Nenhuma inspeção aguardando aprovação."
          />

          <StatusSection
            title="Aguardando correção"
            description="Inspeções devolvidas ao responsável para ajustes."
            icon={RefreshCcw}
            tasks={awaitingCorrection}
            emptyMessage="Nenhuma inspeção aguardando correção."
          />

          <StatusSection
            title="Aprovadas"
            description="Inspeções já revisadas e aprovadas."
            icon={BadgeCheck}
            tasks={approved}
            emptyMessage="Nenhuma inspeção aprovada."
          />
        </div>
      )}

      <Dialog
        open={!!selected}
        onOpenChange={(openState) => {
          if (!openState) {
            closeSelected();
          }
        }}
      >
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              {selected?.template?.title ||
                "Inspeção"}

              {selected?.protocol_number && (
                <Badge
                  variant="outline"
                  className="font-mono text-xs"
                >
                  <Hash className="w-3 h-3 mr-1" />
                  {selected.protocol_number}
                </Badge>
              )}

              {selected && (
                <StatusBadge
                  status={selected.status}
                />
              )}
            </DialogTitle>
          </DialogHeader>

          {selected && (
            <div className="space-y-4">
              {selected.status ===
                "Pendente Correção" &&
                selected.reject_reason && (
                  <Card className="p-3 border-red-500/40 bg-red-500/5">
                    <div className="text-xs uppercase tracking-widest font-semibold text-red-500">
                      Motivo da correção
                    </div>

                    <div className="text-sm mt-1">
                      {selected.reject_reason}
                    </div>
                  </Card>
                )}

              {selected.vehicle && (
                <Card className="p-3 bg-secondary/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Truck className="w-4 h-4 text-primary" />

                    <div className="font-display font-bold text-sm uppercase tracking-tight">
                      Veículo
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">
                        Frota:
                      </span>{" "}
                      <span className="font-mono font-semibold">
                        #
                        {
                          selected.vehicle
                            .fleet_number
                        }
                      </span>
                    </div>

                    <div>
                      <span className="text-muted-foreground">
                        Placa:
                      </span>{" "}
                      <span className="font-mono font-semibold">
                        {
                          selected.vehicle
                            .plate
                        }
                      </span>
                    </div>

                    <div>
                      <span className="text-muted-foreground">
                        Marca/Modelo:
                      </span>{" "}
                      <span>
                        {selected.vehicle.marca
                          ? `${selected.vehicle.marca} `
                          : ""}
                        {
                          selected.vehicle
                            .model
                        }
                      </span>
                    </div>

                    {selected.vehicle
                      .ano_modelo && (
                      <div>
                        <span className="text-muted-foreground">
                          Ano:
                        </span>{" "}
                        {
                          selected.vehicle
                            .ano_modelo
                        }
                      </div>
                    )}

                    <div>
                      <span className="text-muted-foreground">
                        Odômetro:
                      </span>{" "}
                      <span className="font-mono">
                        {Number(
                          selected.vehicle
                            .current_odometer ||
                            0
                        ).toLocaleString(
                          "pt-BR"
                        )}{" "}
                        km
                      </span>
                    </div>

                    <div>
                      <span className="text-muted-foreground">
                        Horímetro:
                      </span>{" "}
                      <span className="font-mono">
                        {Number(
                          selected.vehicle
                            .current_hours ||
                            0
                        ).toLocaleString(
                          "pt-BR"
                        )}{" "}
                        h
                      </span>
                    </div>
                  </div>
                </Card>
              )}

              {loadingLogs && (
                <Card className="p-8 text-center text-sm text-muted-foreground">
                  Carregando dados da
                  inspeção...
                </Card>
              )}

              {!loadingLogs &&
                logs.length === 0 && (
                  <Card className="p-8 text-center text-sm text-muted-foreground">
                    Nenhum envio registrado
                    para esta inspeção.
                  </Card>
                )}

              {!loadingLogs &&
                logs.map(
                  (
                    inspectionLog,
                    logIndex
                  ) => (
                    <Card
                      key={
                        inspectionLog.id ||
                        logIndex
                      }
                      className="p-4"
                    >
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mb-3">
                        <User className="w-3.5 h-3.5" />

                        {
                          inspectionLog.inspector_name
                        }{" "}
                        ·{" "}
                        {formatDate(
                          inspectionLog.timestamp
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs mb-4 p-2 bg-secondary rounded-md">
                        {inspectionLog.started_at && (
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3 h-3 text-primary" />

                            <span className="text-muted-foreground">
                              Início:
                            </span>

                            <span className="font-mono">
                              {formatDate(
                                inspectionLog.started_at
                              )}
                            </span>
                          </div>
                        )}

                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3 h-3 text-primary" />

                          <span className="text-muted-foreground">
                            Envio:
                          </span>

                          <span className="font-mono">
                            {formatDate(
                              inspectionLog.timestamp
                            )}
                          </span>
                        </div>

                        {inspectionLog.latitude !=
                          null &&
                          inspectionLog.longitude !=
                            null && (
                            <div
                              className="flex items-center gap-1.5 sm:col-span-2"
                              data-testid="log-geo"
                            >
                              <MapPin className="w-3 h-3 text-emerald-500" />

                              <span className="text-muted-foreground">
                                GPS:
                              </span>

                              <a
                                className="font-mono text-primary hover:underline"
                                target="_blank"
                                rel="noopener noreferrer"
                                href={`https://www.google.com/maps?q=${inspectionLog.latitude},${inspectionLog.longitude}`}
                              >
                                {Number(
                                  inspectionLog.latitude
                                ).toFixed(
                                  5
                                )}
                                ,{" "}
                                {Number(
                                  inspectionLog.longitude
                                ).toFixed(
                                  5
                                )}
                              </a>
                            </div>
                          )}
                      </div>

                      <div className="space-y-3">
                        {inspectionLog.answers_json?.map(
                          renderAnswer
                        )}
                      </div>

                      {inspectionLog.photos
                        ?.length > 0 && (
                        <div className="mt-4">
                          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-1">
                            <ImageIcon className="w-3 h-3" />

                            Fotos (
                            {
                              inspectionLog
                                .photos
                                .length
                            }
                            )
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {inspectionLog.photos.map(
                              (
                                photo,
                                photoIndex
                              ) => (
                                <img
                                  key={
                                    photoIndex
                                  }
                                  src={
                                    photo
                                  }
                                  onClick={() =>
                                    setZoomPhoto(
                                      photo
                                    )
                                  }
                                  className="w-full h-24 object-cover rounded-md border border-border cursor-zoom-in"
                                  alt={`foto-${photoIndex}`}
                                  data-testid={`log-photo-${photoIndex}`}
                                />
                              )
                            )}
                          </div>
                        </div>
                      )}

                      {inspectionLog.notes && (
                        <div className="mt-3 text-sm p-2 bg-secondary rounded-md">
                          {
                            inspectionLog.notes
                          }
                        </div>
                      )}

                      {inspectionLog.signature && (
                        <div className="mt-4">
                          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-1">
                            <PenLine className="w-3 h-3" />

                            Assinatura do
                            Inspetor
                          </div>

                          <img
                            src={
                              inspectionLog.signature
                            }
                            alt="assinatura"
                            className="max-w-xs h-20 object-contain bg-white rounded-md border border-border p-1"
                            data-testid="log-signature"
                          />
                        </div>
                      )}
                    </Card>
                  )
                )}

              {selected.status ===
                "Enviada" &&
                (rejectMode ? (
                  <Card className="p-4">
                    <label className="text-sm font-semibold mb-2 block">
                      Motivo — descreva o
                      que precisa ser
                      corrigido
                    </label>

                    <Textarea
                      value={reason}
                      onChange={(e) =>
                        setReason(
                          e.target.value
                        )
                      }
                      rows={3}
                      data-testid="reject-reason"
                      placeholder="Ex.: Odômetro digitado incorretamente. Refaça com o valor real do painel."
                    />

                    <div className="flex gap-2 mt-3">
                      <Button
                        variant="destructive"
                        onClick={reject}
                        disabled={
                          processing
                        }
                        data-testid="confirm-reject"
                        className="gap-2"
                      >
                        <X className="w-4 h-4" />

                        {processing
                          ? "Enviando..."
                          : "Solicitar Correção"}
                      </Button>

                      <Button
                        variant="outline"
                        disabled={
                          processing
                        }
                        onClick={() =>
                          setRejectMode(
                            false
                          )
                        }
                      >
                        Cancelar
                      </Button>
                    </div>
                  </Card>
                ) : (
                  <div className="flex flex-wrap gap-2 sticky bottom-0 bg-background border-t border-border pt-3 pb-1">
                    <Button
                      className="flex-1 min-w-[140px] gap-2 bg-emerald-500 hover:bg-emerald-600 text-white"
                      onClick={approve}
                      disabled={
                        processing
                      }
                      data-testid="approve-btn"
                    >
                      <Check className="w-4 h-4" />

                      {processing
                        ? "Processando..."
                        : "Aprovar"}
                    </Button>

                    <Button
                      variant="outline"
                      className="flex-1 min-w-[160px] gap-2 border-red-500/60 text-red-500 hover:bg-red-500/10"
                      onClick={() =>
                        setRejectMode(
                          true
                        )
                      }
                      disabled={
                        processing
                      }
                      data-testid="reject-btn"
                    >
                      <X className="w-4 h-4" />

                      Solicitar Correção
                    </Button>
                  </div>
                ))}

              <div className="flex flex-wrap justify-between gap-2 border-t border-border pt-3">
                <div>
                  {isAdmin && (
                    <Button
                      variant="destructive"
                      className="gap-2"
                      onClick={
                        deleteInspection
                      }
                      disabled={
                        processing
                      }
                      data-testid="delete-inspection-btn"
                    >
                      <Trash2 className="w-4 h-4" />

                      Excluir Inspeção
                    </Button>
                  )}
                </div>

                <Button
                  variant="outline"
                  className="gap-2"
                  data-testid="export-pdf-btn"
                  onClick={exportPdf}
                  disabled={processing}
                >
                  <Download className="w-4 h-4" />

                  Exportar PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!zoomPhoto}
        onOpenChange={(openState) => {
          if (!openState) {
            setZoomPhoto(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl p-2">
          {zoomPhoto && (
            <img
              src={zoomPhoto}
              className="w-full h-auto rounded-md"
              alt="zoom"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
